import { Component, createRef } from 'react';
import { debounce } from 'lodash';
import PropTypes from 'prop-types';
import { error as logError } from 'loglevel';
import i18n from 'src/utils/translate';

const MAX_USERNAME_LENGTH = 20;
const ADD_NEW_MENTION = /(?:^| )@([^ \n]*)$/; // determine if the last "word" start with a `@`

export default class ContentEditor extends Component {
  static propTypes = {
    autoFocus: PropTypes.bool,
    dataTestId: PropTypes.string,
    initialHTML: PropTypes.string,
    keyIntercept: PropTypes.func,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    updateSearch: PropTypes.func,
    toggleButtons: PropTypes.func,
  };

  static defaultProps = {
    autoFocus: false,
    dataTestId: null,
    initialHTML: '',
    keyIntercept: () => {},
    onBlur: () => {},
    onChange: () => {},
    onFocus: () => {},
    updateSearch: () => {},
    toggleButtons: () => {},
  };

  constructor(props) {
    super(props);
    this.editorRef = createRef();
  }

  UNSAFE_componentWillMount() {
    this.keyPressing = {};
    this.debouncedIsMentioning = debounce(this.isMentioning, 200);
  }

  componentDidMount() {
    this._mounted = true;

    if (!this.props.autoFocus) {
      return;
    }

    this.focus();
  }

  shouldComponentUpdate() {
    return false;
  }

  componentWillUnmount() {
    this._mounted = false;
  }

  clearSearch = () => {
    this.props.updateSearch(null);
  }

  focus = () => {
    const windowScrollY = window.scrollY || window.pageYOffset;
    window.scrollTo({
      behavior: 'smooth',
      top: windowScrollY + this.editorRef.current.getBoundingClientRect().top - 120,
    });

    setTimeout(() => {
      this.editorRef.current.focus();
    }, 500);
  }
  focusNow = () => this.editorRef.current.focus();

  isFocused = () => this.editorRef.current === document.activeElement;

  isMentioning = () => {
    if (!this._mounted) return;

    const selection = window.getSelection();
    const node = selection.anchorNode;
    const offset = selection.anchorOffset;

    if (!selection.isCollapsed || !node || node.nodeName === 'A' || !this.isFocused()) {
      this.clearSearch();
      return;
    }

    const str = node.innerHTML || node.nodeValue;

    if (!str) {
      this.clearSearch();
      return;
    }

    const maxString = str.slice(Math.max(offset - MAX_USERNAME_LENGTH - 1, 0), offset);
    const mention = ADD_NEW_MENTION.exec(maxString.replace(/\s/g, ' '));

    this.props.updateSearch(mention && mention[1]);
  }

  onBlur = () => {
    this.keyPressing = {};
    this.clearSearch();
    this.props.onBlur();
  }

  onKeyDown = (event) => {
    this.props.onChange();
    this.props.keyIntercept(event);
    if (event.defaultPrevented) {
      return;
    }

    const key = (window.event) ? event.which : event.keyCode;

    const { childNodes: [firstChild] } = this.editorRef.current;
    const { anchorNode, focusNode, anchorOffset, focusOffset } = window.getSelection();

    this.keyPressing[key] = true;
    switch (key) {
      case 8:
        if (firstChild === focusNode && focusNode === anchorNode && focusOffset === 0 && anchorOffset === 0) {
          event.preventDefault();
          this.keyPressing[key] = false;
        }
        break;
      case 66: // b
        if (this.keyPressing[91] || this.keyPressing[93] || this.keyPressing[17]) {
          event.preventDefault();
          document.execCommand('bold', false, null);
          this.keyPressing[key] = false;
        }
        break;
      case 73: // i
        if (this.keyPressing[91] || this.keyPressing[93] || this.keyPressing[17]) {
          event.preventDefault();
          document.execCommand('italic', false, null);
          this.keyPressing[key] = false;
        }
        break;
      case 85: // u
        if (this.keyPressing[91] || this.keyPressing[93] || this.keyPressing[17]) {
          event.preventDefault();
          document.execCommand('underline', false, null);
          this.keyPressing[key] = false;
        }
        break;
    }
  }

  onKeyUp = (event) => {
    const key = (window.event) ? event.which : event.keyCode;
    this.keyPressing[key] = false;
    this.debouncedIsMentioning();
  }

  onPaste = (event) => {
    event.preventDefault();
    try {
      document.execCommand('inserttext', false, event.clipboardData.getData('text/plain'));
    } catch (e) {
      logError(e);
      if (window.clipboardData) {
        event.returnValue = false;
        const content = window.clipboardData.getData('Text');
        const selection = window.getSelection();
        const range = selection.getRangeAt(0);
        range.deleteContents();
        const newNode = document.createTextNode(content);
        range.insertNode(newNode);
        range.setStartAfter(newNode);
        range.setEndAfter(newNode);
      }
    }
  }

  focusEndOfReply = () => {
    this.editorRef.current.focus();
    if (typeof window.getSelection != 'undefined' && typeof document.createRange != 'undefined') { // Modern browsers
      const range = document.createRange();
      range.selectNodeContents(this.editorRef.current);
      range.collapse(false);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    } else if (typeof document.body.createTextRange != 'undefined') { // Older browsers
      const textRange = document.body.createTextRange();
      textRange.moveToElementText(this.editorRef.current);
      textRange.collapse(false);
      textRange.select();
    }
  }

  addEmoji = (emoji) => {
    if (!this.isFocused()) this.focusEndOfReply();
    const selection = window.getSelection();
    const range = selection.getRangeAt(0);
    range.deleteContents();
    const newNode = document.createTextNode(emoji.native);
    range.insertNode(newNode);
    range.setStartAfter(newNode);
    range.setEndAfter(newNode);
    this.focusEndOfReply();
  }

  startMention = () => {
    if (!this.isFocused()) this.focusEndOfReply();
    const selection = window.getSelection();
    const range = selection.getRangeAt(0);
    range.deleteContents();
    const newNode = document.createTextNode('@');
    range.insertNode(newNode);
    range.setStartAfter(newNode);
    range.setEndAfter(newNode);
    this.focusEndOfReply();
  }

  render() {
    return (
      <div
        className="content-editor"
        contentEditable="true"
        dangerouslySetInnerHTML={{__html: this.props.initialHTML}}
        data-test-id={this.props.dataTestId}
        placeholder={i18n.t('Add a comment here...')}
        ref={this.editorRef}
        onBlur={this.onBlur}
        onClick={() => {
          this.debouncedIsMentioning();
          this.props.toggleButtons();
        }}
        onFocus={this.props.onFocus}
        onKeyDown={this.onKeyDown}
        onKeyUp={(e) => {
          this.onKeyUp(e);
          this.props.toggleButtons();
        }}
        onPaste={this.onPaste}
        onTouchEnd={() => {
          this.debouncedIsMentioning();
          this.props.toggleButtons();
        }}
      />
    );
  }
}
